import { useState, useEffect } from "react";
import { 
  Box, Typography, TextField, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, Pagination,
  CircularProgress, Card, CardContent, Grid, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { Search, Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import { fournisseurService } from 'services/fournisseurService';

const ListeFournisseurs = () => {
  const [fournisseurs, setFournisseurs] = useState([]);
  const [filteredFournisseurs, setFilteredFournisseurs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFournisseurs, setTotalFournisseurs] = useState(0);
  
  // États pour le modal de création
  const [openDialog, setOpenDialog] = useState(false);
  const [newFournisseur, setNewFournisseur] = useState({
    nom: "",
    email: "",
    telephone: "",
    adresse: "",
    type_fournisseur: "autre"
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch des fournisseurs avec le même service que AchatsFournisseurs
  const fetchFournisseurs = async () => {
    try {
      setLoading(true);
      
      // Utiliser le même service que dans AchatsFournisseurs
      const response = await fournisseurService.getAllFournisseurs();
      
      // Le service peut retourner différentes structures
      // Regardons ce qui est retourné
      console.log("Réponse fournisseurs:", response);
      
      // Essayer différentes structures possibles
      let data = [];
      let count = 0;
      
      if (Array.isArray(response)) {
        // Si c'est directement un tableau
        data = response;
        count = response.length;
      } else if (response.results) {
        // Structure avec pagination (results et count)
        data = response.results;
        count = response.count || response.total || data.length;
      } else if (response.data) {
        // Structure avec data
        data = response.data;
        count = Array.isArray(data) ? data.length : 0;
      }
      
      setFournisseurs(data);
      setFilteredFournisseurs(data);
      setTotalFournisseurs(count);
      
      // Si pas de pagination, on montre tout
      setTotalPages(1);
      
      console.log("Fournisseurs chargés:", data.length);
      
    } catch (error) {
      console.error("Erreur chargement fournisseurs:", error);
      toast.error("Erreur lors du chargement des fournisseurs");
      
      // En cas d'erreur, essayer une méthode alternative
      try {
        const token = localStorage.getItem("access_token");
        // const response = await fetch("http://localhost:8000/api/fournisseurs/", {
        //   headers: { 
        //     Authorization: `Bearer ${token}` 
        //   }
        // });
        const response = await fetch("https://sandbox.sgpme.biz/api/fournisseurs/", {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log("Données via fetch:", data);
          
          let fournisseursData = [];
          if (Array.isArray(data)) {
            fournisseursData = data;
          } else if (data.results) {
            fournisseursData = data.results;
          } else if (data.data) {
            fournisseursData = data.data;
          }
          
          setFournisseurs(fournisseursData);
          setFilteredFournisseurs(fournisseursData);
          setTotalFournisseurs(fournisseursData.length);
        }
      } catch (fetchError) {
        console.error("Erreur avec fetch aussi:", fetchError);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFournisseurs();
  }, [page]);

  // Filtrer par nom
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredFournisseurs(fournisseurs);
    } else {
      const filtered = fournisseurs.filter(fournisseur =>
        fournisseur.nom && fournisseur.nom.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFournisseurs(filtered);
    }
  }, [searchTerm, fournisseurs]);

  // Gestionnaire pour ouvrir le dialogue
  const handleOpenDialog = () => {
    setNewFournisseur({
      nom: "",
      email: "",
      telephone: "",
      adresse: "",
      type_fournisseur: "autre"
    });
    setOpenDialog(true);
  };

  // Gestionnaire pour fermer le dialogue
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Gestionnaire pour les changements dans le formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewFournisseur(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gestionnaire pour créer un nouveau fournisseur
  const handleCreateFournisseur = async () => {
    // Validation de base
    if (!newFournisseur.nom.trim()) {
      toast.error("Le nom du fournisseur est requis");
      return;
    }

    try {
      setSubmitting(true);
      
      // Appel au service pour créer le fournisseur
      const response = await fournisseurService.createFournisseur(newFournisseur);
      
      toast.success("Fournisseur créé avec succès");
      
      // Fermer le dialogue
      setOpenDialog(false);
      
      // Rafraîchir la liste des fournisseurs
      await fetchFournisseurs();
      
    } catch (error) {
      console.error("Erreur lors de la création:", error);
      
      // Afficher un message d'erreur plus détaillé si disponible
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.nom) {
          toast.error(`Erreur sur le nom: ${errorData.nom.join(', ')}`);
        } else if (errorData.email) {
          toast.error(`Erreur sur l'email: ${errorData.email.join(', ')}`);
        } else if (errorData.telephone) {
          toast.error(`Erreur sur le téléphone: ${errorData.telephone.join(', ')}`);
        } else {
          toast.error("Erreur lors de la création du fournisseur");
        }
      } else {
        toast.error("Erreur lors de la création du fournisseur");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3}>
      {/* En-tête avec bouton d'ajout */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Liste des Fournisseurs</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleOpenDialog}
        >
          Nouveau Fournisseur
        </Button>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Total Fournisseurs
              </Typography>
              <Typography variant="h5">
                {totalFournisseurs}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Affichés
              </Typography>
              <Typography variant="h5">
                {filteredFournisseurs.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Page
              </Typography>
              <Typography variant="h5">
                {page} / {totalPages}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barre de recherche */}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Rechercher un fournisseur par nom..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
          }}
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Tableau des fournisseurs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Téléphone</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Adresse</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Chargement des fournisseurs...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredFournisseurs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    {fournisseurs.length === 0 
                      ? "Aucun fournisseur trouvé" 
                      : `Aucun fournisseur ne correspond à "${searchTerm}"`}
                  </Typography>
                  {fournisseurs.length === 0 && (
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={handleOpenDialog}
                      sx={{ mt: 1 }}
                    >
                      Créer votre premier fournisseur
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredFournisseurs.map((fournisseur) => (
                <TableRow key={fournisseur.id} hover>
                  <TableCell>
                    <Typography fontWeight="medium">
                      {fournisseur.nom || "Sans nom"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {fournisseur.email || "-"}
                  </TableCell>
                  <TableCell>
                    {fournisseur.telephone || "-"}
                  </TableCell>
                  <TableCell>
                    {fournisseur.adresse ? (
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {fournisseur.adresse}
                      </Typography>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: 
                          fournisseur.type_fournisseur === 'pharmacie' ? 'info.light' :
                          fournisseur.type_fournisseur === 'restaurant' ? 'success.light' :
                          fournisseur.type_fournisseur === 'shop' ? 'warning.light' :
                          'grey.200',
                        color: 
                          fournisseur.type_fournisseur === 'pharmacie' ? 'info.dark' :
                          fournisseur.type_fournisseur === 'restaurant' ? 'success.dark' :
                          fournisseur.type_fournisseur === 'shop' ? 'warning.dark' :
                          'grey.700',
                        fontWeight: 'medium',
                        fontSize: '0.75rem'
                      }}
                    >
                      {fournisseur.type_fournisseur || "Autre"}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bouton pour rafraîchir si problème */}
      {fournisseurs.length === 0 && !loading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
            Aucun fournisseur trouvé. Vérifiez la connexion à l'API.
          </Typography>
          <Button 
            variant="outlined" 
            size="small"
            onClick={fetchFournisseurs}
            sx={{ mr: 1 }}
          >
            Réessayer
          </Button>
          <Button 
            variant="contained" 
            size="small"
            onClick={handleOpenDialog}
          >
            Créer un fournisseur
          </Button>
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Dialogue de création de fournisseur */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Créer un nouveau fournisseur</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nom du fournisseur *"
              name="nom"
              value={newFournisseur.nom}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={newFournisseur.email}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Téléphone"
              name="telephone"
              value={newFournisseur.telephone}
              onChange={handleInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Adresse"
              name="adresse"
              value={newFournisseur.adresse}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={2}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Type de fournisseur</InputLabel>
              <Select
                name="type_fournisseur"
                value={newFournisseur.type_fournisseur}
                onChange={handleInputChange}
                label="Type de fournisseur"
              >
                <MenuItem value="pharmacie">Pharmacie</MenuItem>
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="shop">Magasin</MenuItem>
                <MenuItem value="autre">Autre</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            onClick={handleCreateFournisseur} 
            variant="contained" 
            color="primary"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListeFournisseurs;